import dayjs from 'dayjs'

const PEEK_IN_EXPIRY_TIME = 60 * 60 * 1000 // 1 hour in milliseconds
const PEEK_IN_STORAGE_KEY = 'peekedInRoomDetails'

/**
 * Get all active peek-ins (not expired)
 * @returns {Array} Array of peek-in objects with expiration info
 */
export function getActivePeekIns() {
  try {
    const stored = localStorage.getItem(PEEK_IN_STORAGE_KEY)
    if (!stored) return []

    // Check if it's the old format (single object) or new format (array)
    const parsed = JSON.parse(stored)
    
    // If old format, convert to array
    if (!Array.isArray(parsed)) {
      if (parsed?.clinicLocationId) {
        // Old format - convert to new format
        const newFormat = [{
          ...parsed,
          peekedInAt: parsed.peekedInAt || new Date().toISOString(),
          lastActivityAt: parsed.lastActivityAt || new Date().toISOString(),
          expiresAt: parsed.expiresAt || dayjs().add(1, 'hour').toISOString(),
        }]
        localStorage.setItem(PEEK_IN_STORAGE_KEY, JSON.stringify(newFormat))
        return newFormat
      }
      return []
    }

    // Filter out expired peek-ins
    const now = dayjs()
    const active = parsed.filter((peekIn) => {
      if (!peekIn.expiresAt) return false
      return dayjs(peekIn.expiresAt).isAfter(now)
    })

    // Update storage if any expired
    if (active.length !== parsed.length) {
      localStorage.setItem(PEEK_IN_STORAGE_KEY, JSON.stringify(active))
    }

    return active
  } catch (error) {
    console.error('Error getting active peek-ins:', error)
    return []
  }
}

/**
 * Get peek-in for a specific clinic location
 * @param {string} clinicLocationId - The clinic location ID
 * @returns {Object|null} Peek-in object or null
 */
export function getPeekInForClinic(clinicLocationId) {
  const activePeekIns = getActivePeekIns()
  return activePeekIns.find((peekIn) => peekIn.clinicLocationId === clinicLocationId) || null
}

/**
 * Add or update peek-in for a clinic
 * @param {Object} roomData - Room data with clinicLocationId
 * @returns {Object} Updated peek-in object
 */
export function addOrUpdatePeekIn(roomData) {
  const activePeekIns = getActivePeekIns()
  const now = dayjs()
  const expiresAt = now.add(1, 'hour').toISOString()

  const existingIndex = activePeekIns.findIndex(
    (peekIn) => peekIn.clinicLocationId === roomData.clinicLocationId
  )

  const newPeekIn = {
    ...roomData,
    peekedInAt: existingIndex >= 0 ? activePeekIns[existingIndex].peekedInAt : now.toISOString(),
    lastActivityAt: now.toISOString(),
    expiresAt,
  }

  if (existingIndex >= 0) {
    // Update existing
    activePeekIns[existingIndex] = newPeekIn
  } else {
    // Add new
    activePeekIns.push(newPeekIn)
  }

  localStorage.setItem(PEEK_IN_STORAGE_KEY, JSON.stringify(activePeekIns))
  return newPeekIn
}

/**
 * Update last activity time for a peek-in
 * @param {string} clinicLocationId - The clinic location ID
 */
export function updatePeekInActivity(clinicLocationId) {
  const activePeekIns = getActivePeekIns()
  const now = dayjs()

  const index = activePeekIns.findIndex(
    (peekIn) => peekIn.clinicLocationId === clinicLocationId
  )

  if (index >= 0) {
    // Reset expiration to 1 hour from now
    activePeekIns[index].lastActivityAt = now.toISOString()
    activePeekIns[index].expiresAt = now.add(1, 'hour').toISOString()
    localStorage.setItem(PEEK_IN_STORAGE_KEY, JSON.stringify(activePeekIns))
  }
}

/**
 * Remove peek-in for a clinic
 * @param {string} clinicLocationId - The clinic location ID
 */
export function removePeekIn(clinicLocationId) {
  const activePeekIns = getActivePeekIns()
  const filtered = activePeekIns.filter(
    (peekIn) => peekIn.clinicLocationId !== clinicLocationId
  )
  localStorage.setItem(PEEK_IN_STORAGE_KEY, JSON.stringify(filtered))
}

/**
 * Clear all peek-ins
 */
export function clearAllPeekIns() {
  localStorage.removeItem(PEEK_IN_STORAGE_KEY)
}

/**
 * Get expiration time for a peek-in
 * @param {Object} peekIn - Peek-in object
 * @returns {string|null} ISO string of expiration time or null
 */
export function getPeekInExpiration(peekIn) {
  return peekIn?.expiresAt || null
}

/**
 * Check if peek-in is expired
 * @param {Object} peekIn - Peek-in object
 * @returns {boolean} True if expired
 */
export function isPeekInExpired(peekIn) {
  if (!peekIn?.expiresAt) return true
  return dayjs(peekIn.expiresAt).isBefore(dayjs())
}

/**
 * Get time remaining until expiration
 * @param {Object} peekIn - Peek-in object
 * @returns {number} Milliseconds until expiration (0 if expired)
 */
export function getTimeUntilExpiration(peekIn) {
  if (!peekIn?.expiresAt) return 0
  const now = dayjs()
  const expires = dayjs(peekIn.expiresAt)
  const diff = expires.diff(now)
  return diff > 0 ? diff : 0
}

